import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {Group} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React, {useState} from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import {InstitutionSelectorQueryVariables} from 'institutions/__generated/InstitutionSelectorQuery';
import InstitutionConstants from 'institutions/InstitutionConstants';
import {PresentationalInstitutionSearchInput} from 'institutions/InstitutionSearchInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {OptionData} from 'ui-library/menus/MenuConstants';
import OptionContainer from 'ui-library/menus/OptionContainer';
import OptionContent from 'ui-library/menus/OptionContent';

import {InstitutionSelectorQuery} from './__generated/InstitutionSelectorQuery';

export const INSTITUTION_SELECTOR_QUERY = gql`
  query InstitutionSelectorQuery(
    $searchInput: InstitutionSearchInputInput!
    $limitOffsetInput: LimitOffsetInput!
    $allowUnverified: StrictBoolean!
  ) @skipAudit @engComponent(name: PARTIES) {
    currentTenantInstitutionListing(
      searchInput: $searchInput
      limitOffsetInput: $limitOffsetInput
      allowUnverified: $allowUnverified
    ) {
      institutions {
        ...InstitutionGraphQL
      }
    }
  }
  ${getFragmentWrapper('Institution').fragment}
`;
export type GraphQLInstitution = WithoutTypenames<
  InstitutionSelectorQuery['currentTenantInstitutionListing']['institutions'][number]
>;

const LIMIT_OFFSET = {limit: 15, offset: 0};

interface InstitutionOption extends OptionData {
  value: {
    id: string;
    institution?: GraphQLInstitution;
  };
}

function institutionOption(institution: GraphQLInstitution): InstitutionOption {
  const suffix = institution.licensed ? 'licensed' : institution.verified ? '' : 'unverified';
  const name = suffix ? `${institution.name} (${suffix})` : institution.name;
  return {
    name,
    value: institution,
  };
}

function createUserSuppliedInstitution(term: string): GraphQLInstitution {
  return {
    id: `${InstitutionConstants.USER_SUPPLIED_ID_PREFIX}${term}`,
    name: term,
    fullName: term,
    shortName: '',
    names: [],
    subdomains: [],
    urlIdentifier: '',
    verified: false,
    licensed: false,
    hasUnlimitedSpace: false,
    grantsElnDesktopSync: false,
    adminEmail: '',
    enableExportNotifications: false,
    enableEmailChangeNotifications: false,
  };
}

export interface InstitutionSelectorProps {
  className?: string;
  name?: string;
  value?: GraphQLInstitution;
  onChange: (institution: GraphQLInstitution | null) => void;
  placeholder?: string;
  large?: boolean;
  allowUnverified?: boolean;
  allowUnverifiedSearch?: boolean;
  disabled?: boolean;
  allowLeaving?: boolean;
}

function institutionId(institution: GraphQLInstitution | undefined): string {
  return (institution && institution.id) || '';
}

// Search dropdown for institutions that exposes an input[name=institution] for the ProfileSettingsView form
export default function InstitutionSelector(props: InstitutionSelectorProps): JSX.Element {
  const [query, setQuery] = useState(props.value?.name ?? '');
  const queryVariables = {
    searchInput: {query},
    limitOffsetInput: LIMIT_OFFSET,
    allowUnverified: props.allowUnverifiedSearch ?? false,
  };

  const renderOptionContainer = (optionData: OptionData): JSX.Element => (
    <OptionContainer
      key={optionData.value.id}
      value={optionData.value}
      onSelect={(institution: GraphQLInstitution) => {
        props.onChange(institution);
      }}
    >
      <OptionContent>{optionData.name}</OptionContent>
    </OptionContainer>
  );

  return (
    <Group className={props.className}>
      <SelectDropdown
        className={css(styles.dropdownWrapper)}
        disabled={props.disabled}
        displayName={props.value ? institutionOption(props.value).name : 'No institution selected'}
        menu={
          <BenchlingQuery<InstitutionSelectorQuery, InstitutionSelectorQueryVariables>
            query={INSTITUTION_SELECTOR_QUERY}
            variables={queryVariables}
            renderLoading={() => (
              <PresentationalInstitutionSearchInput
                query={query}
                onChangeQuery={setQuery}
                institutions={[]}
                onSelect={noop}
                isLoading={true}
              />
            )}
            fetchPolicy="cache-and-network"
          >
            {({currentTenantInstitutionListing}) => {
              const institutions: Array<GraphQLInstitution> = [
                ...currentTenantInstitutionListing.institutions,
              ];
              if (props.allowUnverified && query.length > 0 && institutions.length === 0) {
                institutions.push(createUserSuppliedInstitution(query));
              }
              return (
                <PresentationalInstitutionSearchInput
                  query={query}
                  onChangeQuery={setQuery}
                  makeOptionData={institutionOption}
                  institutions={institutions}
                  renderOptionContainer={renderOptionContainer}
                  isLoading={false}
                />
              );
            }}
          </BenchlingQuery>
        }
      />
      <input
        id={props.name}
        className={css(styles.hiddenInput)}
        disabled={props.disabled}
        name={props.name}
        tabIndex={-1}
        type="hidden"
        value={institutionId(props.value)}
      />
      {props.allowLeaving && props.value != null && (
        <Button
          buttonStyle={ButtonStyle.TEXT}
          text="Clear"
          icon="cross-thick"
          onClick={() => {
            props.onChange(null);
          }}
          size={ButtonSize.SMALL}
        />
      )}
    </Group>
  );
}
InstitutionSelector.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: InstitutionConstants.PropTypes.institution,
  onChange: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  large: PropTypes.bool.isRequired,
  allowUnverified: PropTypes.bool.isRequired, // allows creating new unverified institutions
  allowUnverifiedSearch: PropTypes.bool.isRequired, // allows searching for unverified institutions
  disabled: PropTypes.bool,
  allowLeaving: PropTypes.bool,
};

InstitutionSelector.defaultProps = {
  name: 'institution',
  placeholder: 'Institution',
  onChange: noop,
  large: false,
  allowUnverified: true,
  allowUnverifiedSearch: false,
};

const styles = StyleSheet.create({
  dropdownWrapper: {
    flex: '1 1 auto',
  },
  hiddenInput: {
    display: 'none',
  },
});
